import { defineStore } from 'pinia';
import { api } from '@/utils/api';
import { useUserStore } from './UserStore';
import role from '@/utils/role';

export const useCompanyStore = defineStore('company', {
    state: () => ({
        companies: [],
        deletedCompanies: [],
        company: null,
        selectedCompanyId: null,
        members: [],
        roles: [],
        isLoading: false,
        error: null
    }),
    actions: {
        setSelectedCompany(id) {
            this.selectedCompanyId = id;
        },

        async fetchCompanies() {
            try {
                const response = await api.get('/companies/my');
                console.log("Обновление компаний: ", response.data);
                this.companies = response.data;
                if (!this.selectedCompanyId && response.data.length > 0) {
                    this.selectedCompanyId = response.data[0].id;
                }
                return response.data;
            } catch (error) {
                console.error('Error fetching companies:', error);
                return [];
            }
        },

        async fetchCompanyById(id) {
            try {
                await this.fetchCompanies();
                const company = this.companies.find(company => company.id === id);
                this.company = company;

                if (company) {
                    const response = await api.get(`/companies/${id}/members?includePending=true`);
                    console.log(response.data);
                    this.members = response.data.sort((a, b) => a.name?.localeCompare(b.name));
                }
            } catch (error) {
                console.error('Error fetching company:', error);
            }
        },

        async fetchMembersByCompanyId(id) {
            try {
                const response = await api.get(`/companies/${id}/members`);
                console.log(response.data);
                this.members = response.data.sort((a, b) => a.name.localeCompare(b.name));
            } catch (error) {
                console.error('Error fetching company:', error);
            }
        },

        async fetchMySubordinates(id) {
            try {
                let response;
                if (role.getMaxRole(useUserStore().myUserInfo?.roles) === role.OWNER) {
                    console.log('Fetching subordinates for company ID by owner:', id);
                    response = await api.get(`/companies/${id}/members`);
                } else {
                    console.log('Fetching subordinates for company ID:', id);
                    response = await api.get(`/companies/${id}/members/my`);
                }
                console.log(response.data);
                this.members = response.data.sort((a, b) => a.name.localeCompare(b.name));
            } catch (error) {
                console.error('Error fetching company:', error);
            }
        },

        async fetchMySubordinatesByWarehouse(warehouseId) {
            try {
                const response = await api.get(`/companies/warehouses/${warehouseId}/members/my`);
                console.log(response.data);
                this.members = response.data.sort((a, b) => a.name.localeCompare(b.name));
            } catch (error) {
                console.error('Error fetching company:', error);
            }
        },

        async fetchRoles() {
            try {
                const response = await api.get('/roles');
                console.log(response.data);
                this.roles = response.data;
            } catch (error) {
                console.error('Error fetching roles:', error);
            }
        },

        async updateMemberRole(companyId, memberId, roleId, specializationIds) {
            console.log(companyId, memberId, roleId, specializationIds);
            try {
                await api.put(`/companies/${companyId}/members/${memberId}/role`, { roleId: roleId, specializationIds: specializationIds });
                await this.fetchCompanyById(companyId);
            } catch (error) {
                console.error('Error updating member role:', error);
            }
        },

        async assignManager(companyId, memberId, managerId, roleId) {
            try {
                const data = {
                    'memberId': memberId,
                    'managerId': managerId,
                    'roleId': roleId
                };
                console.debug("Assign manager", data);
                // Если managerId null, то удаляем менеджера (делаем сотрудника корневым или просто без менеджера)
                // Если API поддерживает query param или body для этого
                const response = await api.post(`/companies/${companyId}/members/manager`, data);
                console.log(response.data);
                await this.fetchMembersByCompanyId(companyId); // Обновляем список, чтобы перестроить дерево
            } catch (error) {
                console.error('Error assigning manager:', error);
            }
        },

        async detachManager(companyId, memberId) {
            try {
                await api.delete(`/companies/${companyId}/members/manager`, { data: { memberId } });
                await this.fetchMembersByCompanyId(companyId);
            } catch (error) {
                console.error('Error detaching manager:', error);
            }
        },

        async removeMember(companyId, memberId) {
            try {
                const response = await api.delete(`/companies/${companyId}/members/${memberId}`);
                console.log(response.data);
                // Refresh members
                await this.fetchCompanyById(companyId);
            } catch (error) {
                console.error('Error removing member:', error);
            }
        },

        async addMember(data) {
            this.isLoading = true;
            this.error = null;
            console.debug("Add member", data);
            try {
                const response = await api.post(`/companies/members`, data);
                console.log(response.data);
                await this.fetchCompanyById(data.companyId);
                this.isLoading = false;
                return response.data;
            } catch (error) {
                this.error = error.response.data.message;
                this.isLoading = false;
                // throw error.response.data.message;
            }
        },

        async createAndAddUser(data) {
            try {
                const response = await api.post(`/companies/members/new-user`, data);
                console.log(response.data);
                await this.fetchCompanyById(data.companyId);
                this.isLoading = false;
                return response.data;
            } catch (error) {
                this.error = error.response.data.message;
                this.isLoading = false;
                // throw error.response.data.message;
            }
        },

        async createCompany(data) {
            try {
                const response = await api.post('/companies', data);
                console.log(response.data);
                // this.companies.push(response.data);
                // return response.data;
                await this.fetchCompanies();
            } catch (error) {
                console.error('Error creating company:', error);
                throw error;
            }
        },

        async deleteCompany(id) {
            try {
                const response = await api.delete(`/companies/${id}`);
                console.log(response.data);
            } catch (error) {
                console.error('Error deleting company:', error);
            }
        },

        async fetchDeletedCompanies() {
            try {
                const response = await api.get('/companies/deleted');
                console.log('Удалённые компании:', response.data);
                this.deletedCompanies = response.data;
                return response.data;
            } catch (error) {
                console.error('Error fetching deleted companies:', error);
                return [];
            }
        },

        async restoreCompany(id) {
            try {
                const response = await api.post(`/companies/${id}/restore`);
                console.log(response.data);
            } catch (error) {
                console.error('Error restoring company:', error);
            }
        },

        async fetchInvites(companyId) {
            try {
                const response = await api.get(`/companies/${companyId}/invites?page=0&size=50`);
                return response.data;
            } catch (error) {
                console.error('Error fetching invites:', error);
                return { content: [] };
            }
        },

        async createInvite(companyId, data) {
            try {
                const response = await api.post(`/companies/${companyId}/invites`, data);
                return response.data;
            } catch (error) {
                console.error('Error creating invite:', error);
                throw error;
            }
        },

        async revokeInvite(companyId, inviteId) {
            try {
                await api.delete(`/companies/${companyId}/invites/${inviteId}`);
                return true;
            } catch (error) {
                console.error('Error revoking invite:', error);
                throw error;
            }
        }
    }
});
